import { RootState } from '@iwProvider/configureStore';
import { format } from 'date-fns';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { connect } from 'react-redux';

import messages from './ConfirmFundingInfo.messages';
import styles from './ConfirmFundingInfo.module.css';
import {
    getRBLFundingFee,
    getEstimatedTotalRepaymentAmount,
    getEstimatedRepayments
} from '../../../api/lendingAPI/rblCalculatorSelector';
import {
    getDrawdownAmount,
    getRBLDrawdownSweep
} from '../../drawDown/offerConfigurationSelectors';
import { MoneyWithDecimals } from '../../ui/moneyWithDecimals/MoneyWithDecimals';
import moneyStyles from '../../ui/moneyWithDecimals/MoneyWithDecimals.module.css';
import PairItemContainer from '../../ui/pairItemContainer/PairItemContainer';
import { VariableRate } from '../../ui/rate/Rate';

const mapStateToProps = (state: RootState) => {
    const estimatedRepayments = getEstimatedRepayments(state);
    return {
        drawdownAmount: getDrawdownAmount(state),
        feeAmount: getRBLFundingFee(state),
        totalRBLAmount: getEstimatedTotalRepaymentAmount(state),
        sweepFraction: getRBLDrawdownSweep(state),
        firstRepaymentDate: estimatedRepayments?.[0]?.estimated_sweep_date,
        secondRepaymentDate: estimatedRepayments?.[1]?.estimated_sweep_date
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const ConfirmFundingInfo = ({
    drawdownAmount,
    feeAmount,
    totalRBLAmount,
    sweepFraction,
    firstRepaymentDate,
    secondRepaymentDate,
    amount,
    sweep
}: { amount: number; sweep: number } & PropsFromRedux) => {
    return (
        <PairItemContainer>
            <div className={styles.drawdownSummary}>
                <div className={styles.summaryTitle}>
                    <FormattedMessage {...messages.withdraw} />
                </div>

                <div className={styles.selectedValue}>
                    <MoneyWithDecimals
                        className={moneyStyles.MoneyWithDecimals}
                        value={amount || drawdownAmount}
                        decimalWithSeparatorClassName={
                            styles.repayMoneyDecimals
                        }
                    />
                </div>

                <div data-testid="rbl-drawdown-fee">
                    <FormattedMessage
                        {...messages.drawdownFee}
                        values={{
                            feeAmount: (
                                <MoneyWithDecimals
                                    className={styles.highlightedDrawdownValue}
                                    value={feeAmount}
                                />
                            )
                        }}
                    />
                </div>
            </div>

            <div className={styles.repaymentsSummary}>
                <div className={styles.summaryTitle}>
                    <FormattedMessage {...messages.repaymentAmount} />
                </div>

                <div className={styles.selectedValue}>
                    {totalRBLAmount && (
                        <MoneyWithDecimals
                            className={moneyStyles.MoneyWithDecimals}
                            value={totalRBLAmount}
                            decimalWithSeparatorClassName={
                                styles.repayMoneyDecimals
                            }
                        />
                    )}
                </div>

                <div
                    className={styles.summaryTitle}
                    data-testid="rbl-drawdown-sweep"
                >
                    <FormattedMessage
                        {...messages.revenueSweep}
                        values={{
                            sweepPercentage: (
                                <span
                                    className={styles.highlightedDrawdownValue}
                                >
                                    <VariableRate
                                        value={sweep || sweepFraction}
                                        maximumFractionDigits={2}
                                        minimumFractionDigits={0}
                                    />
                                </span>
                            )
                        }}
                    />
                </div>

                <div data-testid="rbl-drawdown-repayment-dates">
                    {firstRepaymentDate && secondRepaymentDate && (
                        <FormattedMessage
                            {...messages.repaymentDates}
                            values={{
                                firstRepaymentDate: (
                                    <span
                                        className={
                                            styles.highlightedDrawdownValue
                                        }
                                    >
                                        <FormattedDate
                                            value={new Date(firstRepaymentDate)}
                                            day="numeric"
                                            month="long"
                                            year="numeric"
                                        />
                                    </span>
                                ),
                                repaymentDayOfMonth: (
                                    <span
                                        className={
                                            styles.highlightedDrawdownValue
                                        }
                                    >
                                        {format(
                                            new Date(secondRepaymentDate),
                                            'do'
                                        )}
                                    </span>
                                )
                            }}
                        />
                    )}
                </div>
            </div>
        </PairItemContainer>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(ConfirmFundingInfo);
